import type { FilterContextItem, IDashboardPermissions, IListedDashboard, IUser } from '@gooddata/sdk-model';
import { AccessorColumnDef, ColumnDef } from '@tanstack/react-table';
import { array, eq, record, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { getFullName } from '@mews-commander/utils';
import { DateTimeService, FeatureFlagService, useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import type { Service } from '@mews-commander/data-access';
import { CUSTOM_CREATORS, EXCLUDED_DASHBOARDS, DEFAULT_GOOD_DATA_LANGUAGE } from './const';
import type { Creator, Dashboard, FilterValues } from './types';

type LocalizeText = ReturnType<typeof useLocalizeText>;

const isAccessorDef = <TData, TValue>(column: ColumnDef<TData, TValue>): column is AccessorColumnDef<TData, TValue> =>
    'accessorFn' in column || 'accessorKey' in column;

export const removeExcludedDashboards = (dashboards: IListedDashboard[], featureFlagService: FeatureFlagService) =>
    dashboards.filter((dashboard) => {
        if (dashboard.tags?.includes('hidden')) return false;
        const item = EXCLUDED_DASHBOARDS.find((i) => i.id === dashboard.identifier);
        if (!item) return true;
        if (item.featureFlag) return featureFlagService.hasFlag(item.featureFlag);
        return false;
    });

const getCreatorId = (createdBy: IListedDashboard['createdBy'], user: IUser) => {
    if (!createdBy) return CUSTOM_CREATORS.Unknown;
    if (createdBy.login === 'mews_provisioning_service') {
        return CUSTOM_CREATORS.Mews;
    } else if (createdBy.login === user.login) {
        return CUSTOM_CREATORS.You;
    }
    return createdBy.login || CUSTOM_CREATORS.Unknown;
};

const getCreatorName = (creatorId: string, fullName: string, localizeText: LocalizeText) => {
    switch (creatorId) {
        case CUSTOM_CREATORS.Mews:
            return localizeText({ textKey: TextKey.Mews });
        case CUSTOM_CREATORS.You:
            return localizeText({ textKey: TextKey.You });
        case CUSTOM_CREATORS.Unknown:
            return localizeText({ textKey: TextKey.Unknown });
        default:
            return fullName || creatorId;
    }
};

export const getUniqueCreators = (dashboards: Dashboard[]): Record<string, Creator> =>
    pipe(
        dashboards,
        array.filter((d) => d.creator.id !== CUSTOM_CREATORS.Unknown),
        array.uniq(eq.contramap((d: Dashboard) => d.creator.id)(string.Eq)),
        array.map((d): [string, Creator] => [d.creator.id, d.creator]),
        record.fromEntries
    );

export const convertDashboards = (
    dashboards: IListedDashboard[],
    user: IUser,
    dashboardsPermissions: Record<string, IDashboardPermissions | null>,
    localizeText: LocalizeText
): Dashboard[] =>
    dashboards.map((dashboard) => {
        const permissions = dashboardsPermissions[dashboard.identifier];
        return {
            id: dashboard.identifier,
            isLocked: dashboard.isLocked ?? false,
            name: dashboard.title,
            creator: {
                id: getCreatorId(dashboard.createdBy, user),
                name: getCreatorName(
                    getCreatorId(dashboard.createdBy, user),
                    getFullName(dashboard.createdBy),
                    localizeText
                ),
            },
            lastModified: dashboard.updated || dashboard.created,
            visibility: dashboard.shareStatus === 'private' ? 'private' : 'public',
            canEdit: !!(dashboard.isLocked ? permissions?.canEditLockedDashboard : permissions?.canEditDashboard),
        };
    });

export const searchDashboards = <TData, TValue>(
    dashboards: TData[],
    inputSearch: string,
    columns: ColumnDef<TData, TValue>[]
) => {
    const search = String(inputSearch).trim().toLowerCase();
    if (!search) return dashboards;

    return dashboards.filter((dashboard, index) =>
        columns.some((column) => {
            if (!isAccessorDef(column)) return false;
            if (!column.enableGlobalFilter) return false;

            const accessor =
                'accessorFn' in column ? column.accessorFn : (item: TData) => item[column.accessorKey as keyof TData];

            const value = String(accessor(dashboard, index)).toLowerCase();
            return value.includes(search);
        })
    );
};

export const filterDashboards = (filters: FilterValues) => {
    return (dashboards: Dashboard[]) =>
        dashboards.filter((dashboard) => {
            if (filters.creator && dashboard.creator.id !== filters.creator) return false;
            if (filters.visibility && dashboard.visibility !== filters.visibility) return false;
            return true;
        });
};

const GD_TIME_FORMAT = 'yyyy-MM-dd HH:mm';
export const sortDashboards = (dashboards: Dashboard[]) => {
    return dashboards.toSorted((a, b) => {
        const dateA = DateTimeService.fromFormat(a.lastModified, GD_TIME_FORMAT);
        const dateB = DateTimeService.fromFormat(b.lastModified, GD_TIME_FORMAT);
        return dateB.toMillis() - dateA.toMillis();
    });
};

export const transformDisplayForms = (filters: FilterContextItem[]) =>
    filters.map((item) => {
        const newItem = { ...item };

        if ('attributeFilter' in newItem && newItem.attributeFilter.displayForm) {
            const { displayForm } = newItem.attributeFilter;

            const transformedDisplayForm = {
                identifier: {
                    id: 'identifier' in displayForm ? displayForm.identifier : '',
                    type: 'label',
                },
            };

            const otherProps = {
                ...newItem.attributeFilter,
                displayForm: undefined,
            };

            return {
                attributeFilter: {
                    ...otherProps,
                    displayForm: transformedDisplayForm,
                },
            };
        }

        return newItem;
    });

export const getServiceName = (service: Service | undefined): string | undefined => {
    return service?.names?.[DEFAULT_GOOD_DATA_LANGUAGE];
};
